import type {DisplayOptionEntity} from 'nutshell-core/utils/map-entity-to-display-option';

import {ContactCardCreateable} from 'shells/peep-cards/contact-card/contact-card-createable/contact-card-createable';
import type {ContactInput} from 'shells/peep-cards/contact-card/types';

import type {
    ContactCreateValidationInput,
    ContactCreateInput,
    EntityRelatedEntityInput,
    PeepMapCreateInput,
    ContactUpdateInput,
} from 'nutshell-graphql-types';

import {getPotentialContacts} from '../../../../core/form/fields/peeps/contacts/rich-contact-select-helpers';
import {useGetContactFormValidation} from '../../../../core/graphql/hooks';

interface CreateProps {
    variant: 'create';
    onCreate: (account: ContactCreateInput) => void;
    onAdd: (input: PeepMapCreateInput) => void;
    alreadyAddedContactIds: string[];
}

interface UpdateProps {
    variant: 'update';
    onUpdate: (account: ContactUpdateInput) => void;
    temporaryContact: ContactInput;
}

type MutationProps = CreateProps | UpdateProps;

interface Props {
    accountId: string;
    onClose: () => void;
    mutationProps: MutationProps;
    isSaving: boolean;
}

export function ContactForm(props: Props) {
    const {refetch: fetchFormValidation} = useGetContactFormValidation();

    const createMutationProps =
        props.mutationProps.variant === 'create' ? props.mutationProps : null;
    const updateMutationProps =
        props.mutationProps.variant === 'update' ? props.mutationProps : null;

    const handleCreateContact = (contact: ContactInput) => {
        if (updateMutationProps) return;
        const newContact: ContactCreateInput = {
            contact: {},
        };

        if (contact.name) {
            newContact.contact.name = contact.name;
        }

        const accountData: EntityRelatedEntityInput = {id: props.accountId};
        if (contact.jobTitle) {
            accountData.relationship = contact.jobTitle;
        }
        newContact.contact.accounts = [accountData];

        if (contact.phoneNumber) {
            newContact.contact.phones = [{number: contact.phoneNumber}];
        }

        if (contact.email) {
            newContact.contact.emailAddresses = [{address: contact.email}];
        }

        createMutationProps?.onCreate?.(newContact);
    };

    const handleAddContact = (input: DisplayOptionEntity) => {
        if (updateMutationProps) return;

        createMutationProps?.onAdd?.({
            contact: input.id,
            account: props.accountId,
        });
    };

    const handleUpdateContact = (contact: ContactInput) => {
        if (!updateMutationProps || !updateMutationProps?.temporaryContact?.id) return;

        updateMutationProps?.onUpdate?.({
            contact: {
                id: updateMutationProps.temporaryContact.id,
                name: contact.name,
                phones: contact.phoneNumber ? [{number: contact.phoneNumber}] : undefined,
                emailAddresses: contact.email ? [{address: contact.email}] : undefined,
                accounts: [
                    {
                        id: props.accountId,
                        relationship: contact.jobTitle === '' ? null : contact.jobTitle,
                    },
                ],
            },
        });
    };

    const onSubmit = createMutationProps ? handleCreateContact : handleUpdateContact;

    const onSelectContact = createMutationProps
        ? handleAddContact
        : (_input: DisplayOptionEntity) => {};

    const onSearchPotentialContacts = createMutationProps
        ? (searchInput: ContactInput) =>
              getPotentialContacts({
                  contactInput: searchInput,
                  relatedAccountIds: [props.accountId],
                  alreadyAddedContactsIds: createMutationProps?.alreadyAddedContactIds ?? [],
              })
        : undefined;
    return (
        <>
            <ContactCardCreateable
                onSelectContact={onSelectContact}
                onSubmit={onSubmit}
                onClose={props.onClose}
                validate={(contactInput: ContactCreateValidationInput) =>
                    fetchFormValidation(contactInput)
                }
                onSearchPotentialContacts={onSearchPotentialContacts}
                hasRelatedAccounts={false}
                showRoleField={true}
                showDescriptionField={false}
                isPotentialContactsPopover={true}
                temporaryContact={updateMutationProps?.temporaryContact}
                isSaving={props.isSaving}
            />
        </>
    );
}
